'use client';

import React, { useEffect, useRef } from 'react';
import './Checkbox.css';

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  /** Label text displayed next to checkbox */
  label?: React.ReactNode;
  /** Alternative to label prop - renders children as label content */
  children?: React.ReactNode;
  /** Indeterminate state (partially checked, e.g., for "select all" checkboxes) */
  indeterminate?: boolean;
  /** Validation state */
  state?: 'default' | 'error';
  /** Checkbox size */
  size?: 'sm' | 'md' | 'lg';
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      children,
      indeterminate = false,
      state = 'default',
      size = 'md',
      checked,
      defaultChecked,
      disabled,
      required,
      className = '',
      style,
      ...rest
    },
    forwardedRef
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (forwardedRef as React.RefObject<HTMLInputElement>) || internalRef;

    // Handle indeterminate state (can only be set via JavaScript)
    // Note: inputRef.current is guaranteed to be set when useEffect runs
    // since React assigns refs during the commit phase before effects execute.
    useEffect(() => {
      inputRef.current!.indeterminate = indeterminate;
    }, [indeterminate, inputRef]);

    const labelContent = label || children;

    const classes = [
      'hi-ui-checkbox',
      `hi-ui-checkbox-size-${size}`,
      state === 'error' && 'hi-ui-checkbox-state-error',
      disabled && 'hi-ui-checkbox-disabled',
      (checked || defaultChecked) && 'hi-ui-checkbox-checked',
      indeterminate && 'hi-ui-checkbox-indeterminate',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={classes} style={style}>
        <input
          ref={inputRef}
          type="checkbox"
          className="hi-ui-checkbox-input"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          required={required}
          aria-invalid={state === 'error' ? true : undefined}
          aria-required={required ? true : undefined}
          {...rest}
        />
        <span className="hi-ui-checkbox-box" aria-hidden="true">
          <svg
            className="hi-ui-checkbox-check"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 6L5 9L10 3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <svg
            className="hi-ui-checkbox-indeterminate"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        {labelContent && <span className="hi-ui-checkbox-label">{labelContent}</span>}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
